// @ts-nocheck -- copied verbatim from the application being replaced.
//
// Quarantined, not exempted. This file has not been rewritten to this project's
// settings and does not pass them; checking it would report errors nobody is
// going to fix in place, and loosening the settings to accommodate it would
// apply that to code written here too. Vite still compiles it -- esbuild strips
// types without checking them.
//
// Delete this line when the file is rewritten. It is the marker for what is left
// to do, and the count of files carrying it is the size of the refactor.
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { CheckCircle, XCircle, AlertCircle, Info, X } from 'lucide-react';
import './toast.css';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastMessage {
  id: string;
  type: ToastType;
  message: string;
  duration?: number;
}

interface ToastContextType {
  toasts: ToastMessage[];
  showToast: (type: ToastType, message: string, duration?: number) => void;
  removeToast: (id: string) => void;
  success: (message: string, duration?: number) => void;
  error: (message: string, duration?: number) => void;
  warning: (message: string, duration?: number) => void;
  info: (message: string, duration?: number) => void;
  dismiss: (id: string) => void;
  dismissLatest: () => void;
  dismissAll: () => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

interface ToastProviderProps {
  children: React.ReactNode;
  maxToasts?: number;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children, maxToasts = 3 }) => {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);
   const dismissAll = useCallback(() => {
    setToasts([]);
  }, []);

  const dismissLatest = useCallback(() => {
    setToasts((prev) => prev.slice(0, -1));
  }, []);

  const showToast = useCallback(
    (type: ToastType, message: string, duration: number = 5000) => {
      const id = `toast-${Date.now()}-${Math.random()}`;
      const newToast: ToastMessage = { id, type, message, duration };

      setToasts((prev) => {
        const updated = [...prev, newToast];
        // Keep only the last maxToasts toasts
        return updated.slice(-maxToasts);
      });

      // Auto-dismiss after duration
      if (duration > 0) {
        setTimeout(() => {
          removeToast(id);
        }, duration);
      }
    },
    [maxToasts, removeToast]
  );

  const success = useCallback((message: string, duration?: number) => {
    showToast('success', message, duration);
  }, [showToast]);

  const error = useCallback((message: string, duration?: number) => {
    showToast('error', message, duration);
  }, [showToast]);

  const warning = useCallback((message: string, duration?: number) => {
    showToast('warning', message, duration);
  }, [showToast]);

  const info = useCallback((message: string, duration?: number) => {
    showToast('info', message, duration);
  }, [showToast]);

  return (
    <ToastContext.Provider value={{ toasts, showToast, removeToast, success, error, warning, info, dismiss: removeToast,dismissLatest, dismissAll }}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
};

interface ToastContainerProps {
  toasts: ToastMessage[];
  onRemove: (id: string) => void;
}

const ToastContainer: React.FC<ToastContainerProps> = ({ toasts, onRemove }) => {
  return (
    <div className="toast-container">
      {toasts.map((toast) => (
        <Toast key={toast.id} toast={toast} onRemove={onRemove} />
      ))}
    </div>
  );
};

interface ToastProps {
  toast: ToastMessage;
  onRemove: (id: string) => void;
}

const Toast: React.FC<ToastProps> = ({ toast, onRemove }) => {
  const [isExiting, setIsExiting] = useState(false);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => {
      onRemove(toast.id);
    }, 300); // Match animation duration
  };

  const getIcon = () => {
    switch (toast.type) {
      case 'success':
        return <CheckCircle className="toast-icon" />;
      case 'error':
        return <XCircle className="toast-icon" />;
      case 'warning':
        return <AlertCircle className="toast-icon" />;
      case 'info':
        return <Info className="toast-icon" />;
      default:
        return null;
    }
  };

  return (
    <div className={`toast toast-${toast.type} ${isExiting ? 'toast-exit' : ''}`}>
      <div className="toast-content">
        <div className="toast-icon-wrapper">{getIcon()}</div>
        <div className="toast-message">{toast.message}</div>
        <button className="toast-close glass-hover g-color" onClick={handleClose} aria-label="Close">
          <X size={18} />
        </button>
      </div>
    </div>
  );
};

// Legacy compatibility wrapper to work with existing toast() calls
export const toast = {
  success: (message: any, duration?: any) => {
    window.dispatchEvent(
      new CustomEvent('show-toast', {
        detail: { type: 'success', message, duration },
      })
    );
  },
  error: (message: any, duration?: any) => {
    window.dispatchEvent(
      new CustomEvent('show-toast', {
        detail: { type: 'error', message, duration },
      })
    );
  },
  warning: (message: string, duration?: number) => {
    window.dispatchEvent(
      new CustomEvent('show-toast', {
        detail: { type: 'warning', message, duration },
      })
    );
  },
  info: (message: string, duration?: any) => {
    window.dispatchEvent(
      new CustomEvent('show-toast', {
        detail: { type: 'info', message, duration },
      })
    );
  },
  // MANUAL DISMISS
  dismiss: (id?: string) => {
    window.dispatchEvent(
      new CustomEvent('dismiss-toast', { detail: { id } })
    );
  },
  // DISMISS ALL
  dismissAll: () => {
    window.dispatchEvent(new CustomEvent('dismiss-toast-all'));
  },
};

// Component to listen for legacy toast events
export const ToastEventListener: React.FC = () => {
  const { showToast, dismiss, dismissAll, dismissLatest } = useToast();

  useEffect(() => {
    const onShow = (e: Event) => {
      const { type, message, duration } = (e as CustomEvent).detail;
      showToast(type, message, duration);
    };

    const onDismiss = (e: Event) => {
      const { id } = (e as CustomEvent).detail || {};
      id ? dismiss(id) : dismissLatest();
    };

    const onDismissAll = () => dismissAll();

    window.addEventListener('show-toast', onShow);
    window.addEventListener('dismiss-toast', onDismiss);
    window.addEventListener('dismiss-toast-all', onDismissAll);

    return () => {
      window.removeEventListener('show-toast', onShow);
      window.removeEventListener('dismiss-toast', onDismiss);
      window.removeEventListener('dismiss-toast-all', onDismissAll);
    };
  }, [showToast, dismiss, dismissAll, dismissLatest]);

  return null;
};